import React, { useState, useEffect, useCallback } from 'react';
import { useChat } from '../context/ChatContext';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  FileText, Download, Filter, RefreshCw, 
  Users, ShoppingCart, Coins, Store, TrendingUp, ChevronDown,
  Search, ArrowUpDown, ChevronLeft, ChevronRight, X, Clock, Calendar,
  Eye, MapPin
} from 'lucide-react';
import { ExportButton } from './ExportUtils';

import { API_BASE_URL as API_URL } from '../utils/apiConfig';

// Report categories and their reports
const REPORT_CATEGORIES = {
  customer: {
    label: 'Customer Reports',
    icon: Users,
    reports: [
      { id: 'customer-data', label: 'Customer Data', endpoint: '/api/reports/customer-data' },
      { id: 'customers-by-visits', label: 'Customers by Visits', endpoint: '/api/reports/customers-by-visits' },
    ]
  },
  transaction: {
    label: 'Transaction Reports',
    icon: ShoppingCart,
    reports: [
      { id: 'transaction-data', label: 'Transaction Data', endpoint: '/api/reports/transaction-data' },
      { id: 'purchase-repeat', label: 'Purchase & Repeat Purchase', endpoint: '/api/reports/purchase-repeat' },
    ]
  },
  points: {
    label: 'Points Reports',
    icon: Coins,
    reports: [
      { id: 'earn-redeem', label: 'Earn/Redeem by Location', endpoint: '/api/reports/earn-redeem' },
    ]
  },
  store: {
    label: 'Store Reports',
    icon: Store,
    reports: [
      { id: 'store-performance', label: 'Store Performance', endpoint: '/api/reports/store-performance' },
    ]
  }
};

// Colors for charts
const CHART_COLORS = ['#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#6366F1', '#14B8A6'];

// Drill-down Modal Component
const DrilldownModal = ({ isOpen, onClose, drilldownData, drilldownType, loading }) => {
  if (!isOpen) return null;

  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num?.toLocaleString('en-IN') || '0';
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gradient-to-r from-purple-900/30 to-pink-900/30">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              {drilldownType === 'location' && <MapPin className="w-5 h-5 text-purple-400" />}
              {drilldownType === 'customer' && <Users className="w-5 h-5 text-purple-400" />}
              {drilldownType === 'month' && <Calendar className="w-5 h-5 text-purple-400" />}
              {drilldownType === 'tier' && <TrendingUp className="w-5 h-5 text-purple-400" />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                {drilldownData?.location || drilldownData?.customer_id || drilldownData?.month || drilldownData?.tier || 'Details'}
              </h2>
              <p className="text-sm text-gray-400">Detailed breakdown</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : drilldownData ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              {drilldownData.summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(drilldownData.summary).map(([key, value]) => (
                    <div key={key} className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-xl font-bold text-white">
                        {key.includes('revenue') || key.includes('spend') || key.includes('value') 
                          ? `₹${formatNumber(value)}` 
                          : formatNumber(value)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Daily Trend Chart */}
              {drilldownData.daily_trend && drilldownData.daily_trend.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-purple-400" />
                    Daily Trend
                  </h3>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={drilldownData.daily_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#9CA3AF" tickFormatter={formatNumber} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                          formatter={(value) => formatNumber(value)}
                        />
                        <Area type="monotone" dataKey="revenue" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} name="Revenue" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Daily Breakdown (for month drilldown) */}
              {drilldownData.daily_breakdown && drilldownData.daily_breakdown.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    Daily Breakdown
                  </h3>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={drilldownData.daily_breakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="date" stroke="#9CA3AF" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                        <YAxis stroke="#9CA3AF" tickFormatter={formatNumber} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                          formatter={(value) => formatNumber(value)}
                        />
                        <Bar dataKey="revenue" fill="#8B5CF6" name="Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Hourly Distribution */}
              {drilldownData.hourly_distribution && drilldownData.hourly_distribution.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-pink-400" />
                    Hourly Distribution
                  </h3>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={drilldownData.hourly_distribution}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="hour" stroke="#9CA3AF" />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                        />
                        <Bar dataKey="transactions" fill="#EC4899" name="Transactions" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Top Customers Table */}
              {drilldownData.top_customers && drilldownData.top_customers.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-400" />
                    Top Customers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 text-left">
                          <th className="pb-2">Customer ID</th>
                          <th className="pb-2">Visits</th>
                          <th className="pb-2">Total Spend</th>
                          <th className="pb-2">Points Earned</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {drilldownData.top_customers.map((c, idx) => (
                          <tr key={idx} className="text-gray-300">
                            <td className="py-2 font-mono text-xs">{c.customer_id}</td>
                            <td className="py-2">{c.visits}</td>
                            <td className="py-2">₹{c.total_spend?.toLocaleString('en-IN')}</td>
                            <td className="py-2">{c.total_points?.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Stores Table */}
              {drilldownData.top_stores && drilldownData.top_stores.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <Store className="w-4 h-4 text-blue-400" />
                    Top Stores
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 text-left">
                          <th className="pb-2">Store</th>
                          <th className="pb-2">Transactions</th>
                          <th className="pb-2">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {drilldownData.top_stores.map((s, idx) => (
                          <tr key={idx} className="text-gray-300">
                            <td className="py-2">{s.store}</td>
                            <td className="py-2">{s.transactions?.toLocaleString('en-IN')}</td>
                            <td className="py-2">₹{s.revenue?.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent Transactions (for customer drilldown) */}
              {drilldownData.recent_transactions && drilldownData.recent_transactions.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-yellow-400" />
                    Recent Transactions
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 text-left">
                          <th className="pb-2">Date</th>
                          <th className="pb-2">Time</th>
                          <th className="pb-2">Store</th>
                          <th className="pb-2">Amount</th>
                          <th className="pb-2">Points</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {drilldownData.recent_transactions.slice(0, 15).map((t, idx) => (
                          <tr key={idx} className="text-gray-300">
                            <td className="py-2">{t.date}</td>
                            <td className="py-2">{t.time}</td>
                            <td className="py-2 max-w-[200px] truncate">{t.store}</td>
                            <td className="py-2">₹{t.amount?.toLocaleString('en-IN')}</td>
                            <td className="py-2">{t.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Store Breakdown (for customer drilldown) */}
              {drilldownData.store_breakdown && drilldownData.store_breakdown.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <Store className="w-4 h-4 text-teal-400" />
                    Store Visits
                  </h3>
                  <div className="h-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={drilldownData.store_breakdown} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis type="number" stroke="#9CA3AF" tickFormatter={formatNumber} />
                        <YAxis dataKey="store" type="category" stroke="#9CA3AF" width={120} tick={{ fontSize: 10 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                          formatter={(value) => `₹${formatNumber(value)}`}
                        />
                        <Bar dataKey="spend" fill="#14B8A6" name="Spend" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Sample Customers (for tier drilldown) */}
              {drilldownData.sample_customers && drilldownData.sample_customers.length > 0 && (
                <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" />
                    Sample Customers
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 text-left">
                          <th className="pb-2">Customer ID</th>
                          <th className="pb-2">Join Date</th>
                          <th className="pb-2">Points Balance</th>
                          <th className="pb-2">Registered Store</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {drilldownData.sample_customers.map((c, idx) => (
                          <tr key={idx} className="text-gray-300">
                            <td className="py-2 font-mono text-xs">{c.customer_id}</td>
                            <td className="py-2">{c.join_date}</td>
                            <td className="py-2">{c.points_balance?.toLocaleString('en-IN')}</td>
                            <td className="py-2 max-w-[200px] truncate">{c.registered_store}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              No data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const { selectedBrand } = useChat();
  const [selectedCategory, setSelectedCategory] = useState('customer');
  const [selectedReport, setSelectedReport] = useState('customer-data');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [stores, setStores] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Drilldown state
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownType, setDrilldownType] = useState('');
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  
  // Filters - Default to last 12 months to ensure data is captured
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('location');
  const [storeCode, setStoreCode] = useState('');
  const [sortBy, setSortBy] = useState('revenue');
  
  // Table state
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Get brand ID for API calls — never fall back to a hardcoded brand.
  // If no brand selected, getBrandId() returns null and callers must skip the request.
  const getBrandId = useCallback(() => {
    if (!selectedBrand) return null;
    return selectedBrand.brand_id || null;
  }, [selectedBrand]);

  // Fetch stores for filter dropdown
  useEffect(() => {
    const bid = getBrandId();
    if (!bid) { setStores([]); return; }
    const fetchStores = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reports/stores?brand_id=${bid}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setStores(data.stores || []);
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };
    fetchStores();
  }, [getBrandId]);

  // Fetch summary stats
  useEffect(() => {
    const bid = getBrandId();
    if (!bid) { setSummary(null); return; }
    const fetchSummary = async () => {
      try {
        const response = await fetch(`${API_URL}/api/reports/summary?brand_id=${bid}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setSummary(data);
        }
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    };
    fetchSummary();
  }, [getBrandId]);

  // Fetch report data
  const fetchReport = useCallback(async () => {
    const bid = getBrandId();
    if (!bid) {
      setReportData(null);
      return;
    }
    setLoading(true);
    try {
      const report = REPORT_CATEGORIES[selectedCategory]?.reports.find(r => r.id === selectedReport);
      if (!report) return;

      const params = new URLSearchParams({
        brand_id: bid,
        start_date: startDate,
        end_date: endDate,
      });

      if (groupBy && (selectedReport === 'customer-data' || selectedReport === 'transaction-data' || selectedReport === 'earn-redeem')) {
        params.append('group_by', groupBy);
      }
      if (storeCode) {
        params.append('store_code', storeCode);
      }
      if (selectedReport === 'store-performance') {
        params.append('sort_by', sortBy);
      }

      const response = await fetch(`${API_URL}${report.endpoint}?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
        setCurrentPage(1);
      } else {
        console.error('Report fetch failed:', response.status);
        setReportData({ data: [], chart_data: [], summary: {} });
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedReport, getBrandId, startDate, endDate, groupBy, storeCode, sortBy]);

  // Fetch report when filters change
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedReport(REPORT_CATEGORIES[category].reports[0].id);
    setGroupBy('location');
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!reportData?.data) return;
    
    const headers = Object.keys(reportData.data[0] || {});
    const csvContent = [
      headers.join(','),
      ...reportData.data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedReport}_${startDate}_${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handle drill-down click
  const handleDrilldown = async (row) => {
    const bid = getBrandId();
    if (!bid) {
      setDrilldownData(null);
      return;
    }
    setDrilldownLoading(true);
    setDrilldownOpen(true);
    
    try {
      let endpoint = '';
      let type = '';
      
      // Determine drill-down type based on current report and group_by
      if (row.location || row.store_name) {
        type = 'location';
        const location = row.location || row.store_name;
        endpoint = `${API_URL}/api/reports/drilldown/location?brand_id=${bid}&location=${encodeURIComponent(location)}&start_date=${startDate}&end_date=${endDate}`;
      } else if (row.tier) {
        type = 'tier';
        endpoint = `${API_URL}/api/reports/drilldown/tier?brand_id=${bid}&tier=${encodeURIComponent(row.tier)}&start_date=${startDate}&end_date=${endDate}`;
      } else if (row.month) {
        type = 'month';
        endpoint = `${API_URL}/api/reports/drilldown/month?brand_id=${bid}&month=${row.month}`;
      } else if (row.customer_id) {
        type = 'customer';
        endpoint = `${API_URL}/api/reports/drilldown/customer?brand_id=${bid}&customer_id=${row.customer_id}&start_date=${startDate}&end_date=${endDate}`;
      }
      
      if (endpoint) {
        setDrilldownType(type);
        const response = await fetch(endpoint, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setDrilldownData(data);
        }
      }
    } catch (error) {
      console.error('Error fetching drilldown:', error);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Handle chart click for drill-down
  const handleChartClick = (data) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const payload = data.activePayload[0].payload;
      handleDrilldown(payload);
    }
  };

  // Table sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get filtered and sorted data
  const getProcessedData = () => {
    if (!reportData?.data) return [];
    
    let data = [...reportData.data];
    
    // Filter by search term
    if (searchTerm) {
      data = data.filter(row => 
        Object.values(row).some(val => 
          String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
    
    // Sort
    if (sortColumn) {
      data.sort((a, b) => {
        const aVal = a[sortColumn];
        const bVal = b[sortColumn];
        const modifier = sortDirection === 'asc' ? 1 : -1;
        
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return (aVal - bVal) * modifier;
        }
        return String(aVal).localeCompare(String(bVal)) * modifier;
      });
    }
    
    return data;
  };

  // Pagination
  const processedData = getProcessedData();
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Format number
  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)}L`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num?.toLocaleString('en-IN') || '0';
  };

  // Get chart data key
  const getChartKey = () => {
    if (selectedReport === 'customer-data') return groupBy === 'location' ? 'location' : groupBy === 'tier' ? 'tier' : 'month';
    if (selectedReport === 'transaction-data') return groupBy === 'location' ? 'location' : groupBy === 'month' ? 'month' : 'day';
    if (selectedReport === 'earn-redeem') return groupBy === 'location' ? 'location' : 'month';
    if (selectedReport === 'customers-by-visits') return 'visits';
    if (selectedReport === 'purchase-repeat') return 'location';
    if (selectedReport === 'store-performance') return 'store_name';
    return 'location';
  };

  // Get chart value key
  const getChartValueKey = () => {
    if (selectedReport === 'customer-data') return 'total_customers';
    if (selectedReport === 'transaction-data') return 'total_purchase';
    if (selectedReport === 'earn-redeem') return 'total_earn_points';
    if (selectedReport === 'customers-by-visits') return 'total_customers';
    if (selectedReport === 'purchase-repeat') return 'total_loyalty_purchase';
    if (selectedReport === 'store-performance') return 'total_revenue';
    return 'total_customers';
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl">
            <FileText className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Reports</h1>
            <p className="text-gray-400 text-sm">Comprehensive analytics for {selectedBrand?.name || 'All Brands'}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={exportToCSV}
            disabled={!reportData?.data?.length}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          
          {/* Full Report Export */}
          <ExportButton 
            brandId={selectedBrand?.brand_id || getBrandId()}
            exportType={selectedReport === 'customer-data' ? 'customers' : 
                       selectedReport === 'transaction-data' ? 'transactions' : 
                       selectedReport === 'store-performance' ? 'stores' : 'customers'}
            label="Download Full Report"
            filename={`${selectedReport}_full_${new Date().toISOString().split('T')[0]}.csv`}
          />
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Users className="w-4 h-4" />
              Total Customers
            </div>
            <p className="text-xl font-bold text-purple-400">{formatNumber(summary.total_customers)}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <ShoppingCart className="w-4 h-4" />
              Transactions
            </div>
            <p className="text-xl font-bold text-pink-400">{formatNumber(summary.total_transactions)}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <TrendingUp className="w-4 h-4" />
              Revenue
            </div>
            <p className="text-xl font-bold text-green-400">₹{formatNumber(summary.total_revenue)}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Coins className="w-4 h-4" />
              Points Earned
            </div>
            <p className="text-xl font-bold text-yellow-400">{formatNumber(summary.total_points)}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Users className="w-4 h-4" />
              Active Customers
            </div>
            <p className="text-xl font-bold text-blue-400">{formatNumber(summary.active_customers)}</p>
          </div>
          <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
              <Store className="w-4 h-4" />
              Stores
            </div>
            <p className="text-xl font-bold text-teal-400">{formatNumber(summary.total_stores)}</p>
          </div>
        </div>
      )}

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(REPORT_CATEGORIES).map(([key, cat]) => {
          const Icon = cat.icon;
          return (
            <button
              key={key}
              onClick={() => handleCategoryChange(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                selectedCategory === key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`category-${key}`}
            >
              <Icon className="w-4 h-4" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Report Selector & Filters */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Report Type */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-gray-400 mb-1">Report Type</label>
            <div className="relative">
              <select
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white appearance-none cursor-pointer focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="report-selector"
              >
                {REPORT_CATEGORIES[selectedCategory]?.reports.map(report => (
                  <option key={report.id} value={report.id}>{report.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Date Range */}
          <div className="min-w-[150px]">
            <label className="block text-sm text-gray-400 mb-1">Start Date</label>
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="start-date"
              />
            </div>
          </div>
          <div className="min-w-[150px]">
            <label className="block text-sm text-gray-400 mb-1">End Date</label>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="end-date"
              />
            </div>
          </div>

          {/* Group By (for applicable reports) */}
          {(selectedReport === 'customer-data' || selectedReport === 'transaction-data' || selectedReport === 'earn-redeem') && (
            <div className="min-w-[150px]">
              <label className="block text-sm text-gray-400 mb-1">Group By</label>
              <div className="relative">
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white appearance-none cursor-pointer focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="group-by"
                >
                  <option value="location">Location</option>
                  {selectedReport === 'customer-data' && <option value="tier">Tier</option>}
                  <option value="month">Month</option>
                  {selectedReport === 'transaction-data' && <option value="day">Day</option>}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Store Filter */}
          <div className="min-w-[200px]">
            <label className="block text-sm text-gray-400 mb-1">Location</label>
            <div className="relative">
              <select
                value={storeCode}
                onChange={(e) => setStoreCode(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white appearance-none cursor-pointer focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="store-filter"
              >
                <option value="">All Locations</option>
                {stores.map(store => (
                  <option key={store.store_code} value={store.store_code}>
                    {store.store_name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Sort By (for store performance) */}
          {selectedReport === 'store-performance' && (
            <div className="min-w-[150px]">
              <label className="block text-sm text-gray-400 mb-1">Sort By</label>
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white appearance-none cursor-pointer focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="sort-by"
                >
                  <option value="revenue">Revenue</option>
                  <option value="customers">Customers</option>
                  <option value="bills">Bills</option>
                  <option value="atv">ATV</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Apply Button */}
          <button
            onClick={fetchReport}
            disabled={loading}
            className="flex items-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
            data-testid="apply-filters"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />}
            Apply Filters
          </button>
        </div>
      </div>

      {/* Chart Section */}
      {reportData?.data?.length > 0 && (
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            {REPORT_CATEGORIES[selectedCategory]?.reports.find(r => r.id === selectedReport)?.label} - Chart View
            <span className="text-xs text-gray-400 font-normal ml-2">(Click bars for details)</span>
          </h3>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              {selectedReport === 'earn-redeem' ? (
                <BarChart data={reportData.data.slice(0, 15)} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} onClick={handleChartClick} style={{cursor: 'pointer'}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey={getChartKey()} 
                    stroke="#9CA3AF" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis stroke="#9CA3AF" tickFormatter={formatNumber} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(value) => formatNumber(value)}
                  />
                  <Legend />
                  <Bar dataKey="total_earn_points" name="Earn Points" fill="#8B5CF6" style={{cursor: 'pointer'}} />
                  <Bar dataKey="total_redeem_points" name="Redeem Points" fill="#10B981" />
                  <Bar dataKey="total_bonus_points" name="Bonus Points" fill="#F59E0B" />
                </BarChart>
              ) : (
                <BarChart data={reportData.data.slice(0, 15)} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} onClick={handleChartClick} style={{cursor: 'pointer'}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey={getChartKey()} 
                    stroke="#9CA3AF" 
                    angle={-45} 
                    textAnchor="end" 
                    height={80}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis stroke="#9CA3AF" tickFormatter={formatNumber} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                    formatter={(value) => formatNumber(value)}
                  />
                  <Legend />
                  <Bar dataKey={getChartValueKey()} name={getChartValueKey().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} fill="#8B5CF6" style={{cursor: 'pointer'}} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
        {/* Table Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-400" />
            Data Table
            {reportData?.data && (
              <span className="text-sm text-gray-400 font-normal ml-2">
                ({processedData.length} records)
              </span>
            )}
          </h3>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              data-testid="table-search"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : reportData?.data?.length > 0 ? (
            <table className="w-full" data-testid="reports-table">
              <thead className="bg-gray-800/50">
                <tr>
                  {Object.keys(reportData.data[0] || {}).map((key) => (
                    <th
                      key={key}
                      className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white transition-colors"
                      onClick={() => handleSort(key)}
                    >
                      <div className="flex items-center gap-2">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {paginatedData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-800/30 transition-colors group">
                    {Object.entries(row).map(([key, value], cellIdx) => (
                      <td key={cellIdx} className="px-4 py-3 text-sm">
                        {typeof value === 'number' 
                          ? (key.includes('purchase') || key.includes('revenue') || key.includes('amount'))
                            ? `₹${value.toLocaleString('en-IN')}`
                            : value.toLocaleString('en-IN')
                          : value
                        }
                      </td>
                    ))}
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => handleDrilldown(row)}
                        className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2 py-1 bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 rounded transition-all text-xs"
                        data-testid={`drilldown-btn-${idx}`}
                      >
                        <Eye className="w-3 h-3" />
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Totals Row */}
              {reportData?.totals && (
                <tfoot className="bg-purple-900/20 border-t-2 border-purple-500">
                  <tr>
                    {Object.keys(reportData.data[0] || {}).map((key, idx) => (
                      <td key={idx} className="px-4 py-3 text-sm font-bold text-purple-300">
                        {idx === 0 ? 'Total' : (
                          reportData.totals[key] !== undefined
                            ? (key.includes('purchase') || key.includes('revenue') || key.includes('amount'))
                              ? `₹${reportData.totals[key]?.toLocaleString('en-IN')}`
                              : reportData.totals[key]?.toLocaleString('en-IN')
                            : ''
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p>No data available</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-800 flex items-center justify-between">
            <p className="text-sm text-gray-400">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, processedData.length)} of {processedData.length} records
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="px-4 py-2 bg-gray-800 rounded-lg text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drilldown Modal */}
      <DrilldownModal
        isOpen={drilldownOpen}
        onClose={() => {
          setDrilldownOpen(false);
          setDrilldownData(null);
        }}
        drilldownData={drilldownData}
        drilldownType={drilldownType}
        loading={drilldownLoading}
      />
    </div>
  );
};

export default Reports;
